/**
 * exportService.ts
 * Excel export via xlsx (SheetJS) — browser-native, no Node polyfills needed.
 * PDF export via jsPDF + autoTable.
 */

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import type { ReviewForm, ReviewCycle } from '../types';

type ReviewFormWithScore = ReviewForm & { finalScore?: number; calibratedScore?: number };

// ─── Excel ────────────────────────────────────────────────────────────────────

export function exportToExcel(forms: ReviewFormWithScore[], cycle: ReviewCycle): void {
  const rows = forms.map(f => ({
    Name: f.revieweeName,
    'Reviewer': f.reviewerName || f.reviewerId,
    Status: f.status,
    'Computed Score': f.computedScore?.toFixed(2) ?? '',
    'Calibrated Score': f.calibratedScore?.toFixed(2) ?? '',
    'Discretion Adj.': f.discretionAdjustment?.toFixed(2) ?? '',
    'Final Score': f.finalScore?.toFixed(2) ?? '',
    'OPTICS Score': getOpticsDisplay(f),
    'Character Score': getCharacterDisplay(f),
    'Self-review Submitted': f.selfReview?.submittedAt
      ? format(timestampToDate(f.selfReview.submittedAt), 'dd/MM/yyyy')
      : '',
    'Manager Submitted': f.managerReview?.submittedAt
      ? format(timestampToDate(f.managerReview.submittedAt), 'dd/MM/yyyy')
      : '',
    'Approved At': f.approvedAt
      ? format(timestampToDate(f.approvedAt), 'dd/MM/yyyy')
      : '',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'EPR Results');

  // Auto column widths
  const cols = Object.keys(rows[0] ?? {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
  ws['!cols'] = cols;

  XLSX.writeFile(wb, `EPR_${cycle.name}_${format(new Date(), 'yyyyMMdd')}.xlsx`);
}

// ─── PDF ──────────────────────────────────────────────────────────────────────

export function exportToPDF(forms: ReviewFormWithScore[], cycle: ReviewCycle): void {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  pdf.setFontSize(14);
  pdf.text(`EPR Results — ${cycle.name}`, 14, 16);
  pdf.setFontSize(9);
  pdf.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`, 14, 22);

  autoTable(pdf, {
    startY: 28,
    head: [[
      'Name', 'Status', 'Computed', 'Calibrated', 'Adj.', 'Final',
    ]],
    body: forms.map(f => [
      f.revieweeName,
      f.status,
      f.computedScore?.toFixed(2) ?? '—',
      f.calibratedScore?.toFixed(2) ?? '—',
      f.discretionAdjustment != null ? (f.discretionAdjustment >= 0 ? '+' : '') + f.discretionAdjustment.toFixed(2) : '—',
      f.finalScore?.toFixed(2) ?? '—',
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [1, 17, 50], textColor: 255 },
    alternateRowStyles: { fillColor: [240, 242, 247] },
  });

  pdf.save(`EPR_${cycle.name}_${format(new Date(), 'yyyyMMdd')}.pdf`);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getOpticsDisplay(f: ReviewForm): string {
  const scores = f.managerReview?.opticsScores ?? f.selfReview?.opticsScores ?? {};
  const vals = Object.values(scores).filter(v => v > 0);
  if (!vals.length) return '';
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
}

function getCharacterDisplay(f: ReviewForm): string {
  const scores = f.managerReview?.characterScores ?? f.selfReview?.characterScores ?? {};
  const vals = Object.values(scores).filter(v => v > 0);
  if (!vals.length) return '';
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
}

/**
 * Coerce a Firestore Timestamp (or any common variant) to a Date.
 * Same narrow union as CycleManagement.tsx — kept inline here to keep
 * exportService dependency-free; promote to a shared util only when a
 * 3rd consumer appears.
 */
type TimestampLike =
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | Date
  | string
  | number;

function timestampToDate(ts: TimestampLike): Date {
  if (ts instanceof Date) return ts;
  if (typeof ts === 'string' || typeof ts === 'number') return new Date(ts);
  if ('toDate' in ts && typeof ts.toDate === 'function') return ts.toDate();
  if ('seconds' in ts && typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  return new Date(NaN);
}
